import { Form, Button, Typography, Slider, Select, Card, Checkbox, Input, Divider, notification, message, Result } from 'antd';

import React, { useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { SendToBackend, FetchFromBackend, ErrorConnectionFailed } from '../../shared/HelperContactBackend';

import '../../styles/Stepper.css';

var md5 = require('md5');

const { Option } = Select;
const { Text } = Typography;

const COMPLETION_CODES = {
    chatgpt: "You took the wrong survey. Please follow the study's instructions, or return your submission.",
    gemini: "CLVB1YWV",
    claude: "C1AUVLJC",
    deepseek: "C1KT27C8",
    mistral : "CG4FDRPE", 
    grok: "CXUWZCBL"
};

function getCompletionCode(platform) {
    return COMPLETION_CODES[platform] ?? "SURVEY-UNKNOWN";
}

export const FormSurvey = ({ next, useMobileVersion, platform, sessionId, emailIdentifier, donationOptions, demographicSurveyQuestions, usageSurveyQuestions, techsavvinessSurveyQuestions, setAmountToBePayed, amountTobePayed }) => {

    const [surveyStage, setSurveyStage] = useState("demographics");
    const [completionCode, setCompletionCode] = useState(null);
    const [demographicsAnswers, setDemographicsAnswers] = useState(null);
    const [demographicsForm] = Form.useForm();
    const [usageForm] = Form.useForm();

    const defaultAgreementSlider = {
        0: {
            label: 'Strongly\nDisagree',
            style: { fontSize: useMobileVersion ? "x-small" : "medium" }
        },
        25: {
            label: 'Slightly\nDisagree',
            style: { fontSize: useMobileVersion ? "x-small" : "medium" }
        },
        50: {
            label: 'Neutral',
            style: { fontSize: useMobileVersion ? "x-small" : "medium" }
        },
        75: {
            label: 'Slightly\nAgree',
            style: { fontSize: useMobileVersion ? "x-small" : "medium" }
        },
        100: {
            label: 'Strongly\nAgree',
            style: { fontSize: useMobileVersion ? "x-small" : "medium" }
        }
    };

    // expects an array of options (strings)    
    function GetSliderConfig(options) {
        const n = options?.length ?? 5;
        const safeOptions = (n >= 2) ? options : ["Strongly Disagree","Disagree","Neutral","Agree","Strongly Agree"];

        const step = 100 / (safeOptions.length - 1);

        const fontSize =
            window.innerWidth <= 850 ? "xx-small" :
            window.innerWidth <= 980 ? "small" :
            "medium";

        const marks = {};
        safeOptions.forEach((opt, i) => {
            const pos = Math.round(i * step);
            marks[pos] = {
            label: String(opt).replace(" ", "\n"),
            style: { fontSize }
            };
        });

        const defaultValue = Math.round((safeOptions.length - 1) / 2) * step; // midpoint

        return { marks, step, defaultValue };
    }



    // Generalized the percentile to index conversion for point-scale questions
    function PercentileToIndex(p, nOptions) {
        if (!Number.isFinite(p) || nOptions <= 1) return 0;

        const step = 100 / (nOptions - 1);
        // round to nearest step, then convert to index
        const idx = Math.round(p / step);

        return Math.min(nOptions - 1, Math.max(0, idx));
    }

    // Expects an array of questions, form values, and an array of answers.
    function AddAnswersToList(questions, answers, answerList) {
        questions.forEach((questionObject, index) => {
            const question_id = questionObject.question_id;
            var answerObject = {
                "session_id": sessionId.session_id,
                "question_id": question_id,
                "answer": null
            }
            if ((typeof answers[question_id + "PreferNotToSay"] !== 'undefined') && (answers[question_id + "PreferNotToSay"])) {
                answerObject.answer = "";
            }
            else if ((typeof answers[question_id] !== 'undefined') && (answers[question_id] === "Other: Specify")) {
                answerObject.answer = answers[question_id + "CustomValue"];
            }
            else {
                switch (questionObject.question_type) {
                    case "multichoice":
                        answerObject.answer = answers[question_id];
                        break;
                    case "point-scale":
                        const n = questionObject.options.length;
                        const answerIndex = PercentileToIndex(answers[question_id], n);
                        answerObject.answer = questionObject.options[answerIndex];
                        break;
                    case "select_all_apply":
                        answerObject.answer = answers[question_id].toString();
                        break;
                }
            }
            answerList.push(answerObject);
        });
    };

    // Expects form values
    function SubmitSurveyAnswers(usageAnswers) {
        // create placeholder for donation (this was needed bc of the foreign key constraint on answers)
        const donation_placeholder = JSON.stringify({
            session_id: sessionId.session_id,
            platform: platform,
            donated_data: {},          // placeholder
            donated_options: {},       // placeholder
            source: "survey",          // placeholder
            email_md5: md5(emailIdentifier),
        });

        // Payment placeholder (survey completed, bonus 0, no donation options yet)
        const payment_placeholder = JSON.stringify({
            email: emailIdentifier,
            email_md5: md5(emailIdentifier),
            platform: platform,
            bonus_payment: 0,
            donation_options: {},
            survey_completed: true,
        });

        // answers payload
        const answerList = [];
        AddAnswersToList(demographicSurveyQuestions, demographicsAnswers, answerList);
        AddAnswersToList(usageSurveyQuestions, usageAnswers, answerList);

        const answers_payload = JSON.stringify({ answers: answerList });

        // Chain the requests: donation -> payment -> answers[last bc of foreign key constraint]
        SendToBackend(
            donation_placeholder,
            "donations/upsert/",
            () => {
            SendToBackend(
                payment_placeholder,
                "payments/upsert/", 
                () => {
                SendToBackend(
                    answers_payload,
                    "answers/",
                    () => {
                        // show done screen instead of jumping to next step 
                        setCompletionCode(getCompletionCode(platform));
                        setSurveyStage("done");
                        window.scrollTo(0, 0);
                    },
                    ErrorConnectionFailed
                );

                },
                ErrorConnectionFailed
            );
            },
            ErrorConnectionFailed
        );
}


    // Expects an integer
    const openNotificationQuestionsUnanswered = (nQuestionsUnanswered) => {
        notification["error"]({
            message: <Text strong>{'Please complete all questions'}</Text>,
            description:
                <Text className="Text">
                    {'You have left '
                        + nQuestionsUnanswered +
                        ' question(s) unanswered. Please fill all questions.\n\n'}
                </Text>,
            duration: 10
        });
    };

    // Expects an integer
    const openNotificationTextFieldsEmpty = (nTextFieldsEmpty) => {
        notification["error"]({
            message: <Text strong>{'Please fill in "Specify: Other" fields'}</Text>,
            description:
                <Text className="Text">
                    {'On questions for which you have selected "Other: Specify", \
please type your specified answer in the box provided below the question.\
We detect that you have left empty "Other: Specify" text boxes on '
                        + nTextFieldsEmpty +
                        ' question(s).'}
                </Text>,
            duration: 10
        });
    };

    // Notification when user tries to skip survey but cannot
    const openNotificationCannotSkip = () => {
        notification["error"]({
            message: <Text strong>Survey not yet completed</Text>,
            description: (
            <Text className="Text">
                We could not confirm that you have already completed the survey for {platform}.
                Please complete the survey, or try again later.
            </Text>
            ),
            duration: 8
        });
    };
    // Expects form values, an array of questions, and a function which accepts no arguments.
    function VerifyQuestionsCompleted(values, surveyQuestions, onSuccess) {

        //console.log("Verifying questions completed. Values:", values);
        var nQuestionsUnanswered = 0;
        var nTextFieldsEmpty = 0;
        surveyQuestions.forEach((questionObject, index) => {
            const question_id = questionObject.question_id;
            // Check whether the question is unanswered.
            if ((values[question_id] === undefined) && !(values[question_id + "PreferNotToSay"])) {
                nQuestionsUnanswered++;
            }
            // Check whether the specify text field was left empty, if it was used
            else if (!(values[question_id + "PreferNotToSay"]) && (values[question_id] === "Other: Specify") && (values[question_id + "CustomValue"] === undefined)) {
                //console.log("Question id: ", question_id, "Undefined text box: ", values[question_id + "CustomValue"]);
                nTextFieldsEmpty++;
            }
        });
        if (nQuestionsUnanswered > 0) {
            openNotificationQuestionsUnanswered(nQuestionsUnanswered);
        }
        if (nTextFieldsEmpty > 0) {
            openNotificationTextFieldsEmpty(nTextFieldsEmpty);
        }
        if ((nQuestionsUnanswered === 0) && (nTextFieldsEmpty === 0)) {
            window.scrollTo(0, 0);
            onSuccess();
        }
    }

    // Expects form values
    const onFinishDemographics = (values) => {
        VerifyQuestionsCompleted(values, demographicSurveyQuestions, () => {
            setSurveyStage("usage");
            setDemographicsAnswers(values);
        })
        //console.log(values);
    };

    // Expects form values
    const onFinishUsage = (values) => {
        VerifyQuestionsCompleted(values, usageSurveyQuestions, () => {
            SubmitSurveyAnswers(values);
        })
        //console.log("Finish Usage:", values);

    };

    function OptOutCheckbox(question_id, setQuestionDisabled, questionCategory) {
        if (questionCategory === "platform-use" || questionCategory === "demographics") {
            return null;   // no opt-out option for platform-use questions
        }

        return (
            <Form.Item name={question_id + "PreferNotToSay"} valuePropName="checked">
                <Checkbox
                    className={useMobileVersion ? "SurveyOptOutCheckboxMobile" : "SurveyOptOutCheckbox"}

                    onChange={(e) => { setQuestionDisabled(e.target.checked) }}>
                    {useMobileVersion ? "Prefer not to say" : "Prefer\nnot to\nsay"}
                </Checkbox>
            </Form.Item>);
    }

    function SliderQuestion(question_id, question, options, question_category) {
        const allowOptOut = question_category !== "platform-use";
        const [sliderDisabled, setSliderDisabled] = useState(/* keep your logic */);

        const { marks, step, defaultValue } = GetSliderConfig(options);

        return (
            <div className="SurveyQuestionPadding" style={{ clear: "both" }}>
            <Text>{question}</Text>

            <div>
                <div className={useMobileVersion ? "SurveyQuestionMobile" : "SurveySlider"}>
                <Form.Item name={question_id} preserve initialValue={defaultValue}>
                    <Slider
                    marks={marks}
                    included={false}
                    defaultValue={defaultValue}
                    step={step}
                    min={0}
                    max={100}
                    tooltip={{ open: false }}  // newer antd; if yours is older keep tooltipVisible={false}
                    disabled={sliderDisabled}
                    />
                </Form.Item>
                </div>

                {allowOptOut && (
                <div>{OptOutCheckbox(question_id, setSliderDisabled, question_category)}</div>
                )}
            </div>
            </div>
        );
    }
    function SelectOneDropdown(options, setSelectedValue, selectDisabled) {
        return (
            <Select
                disabled={selectDisabled}
                onChange={(value) => {
                    setSelectedValue(value);
                }}>
                {options.map((item) => (
                    <Option key={item} value={item}>{item}</Option>
                ))}
            </Select>
        );
    }

    function SelectAllDropdown(options, selectDisabled) {
        const [optionsWithCustom, setOptionsWithCustom] = useState(options);
        const [enteredValue, setEnteredValue] = useState(null);
        return (
            <Select
                mode="multiple"
                disabled={selectDisabled}
                dropdownRender={menu => (
                    <div>
                        {menu}
                        <Divider style={{ margin: '4px 0' }} />
                        <div style={{ display: 'flex', flexWrap: 'nowrap', padding: 8 }}>
                            <Input style={{ flex: 'auto' }} onChange={(e) => {
                                setEnteredValue(e.target.value);
                            }} />
                            <a
                                style={{ flex: 'none', padding: '8px', display: 'block', cursor: 'pointer' }}
                                onClick={() => {
                                    if (enteredValue === null) {
                                        message.error('Please enter a custom value.');
                                    }
                                    else if (enteredValue.includes(',')) {
                                        message.error('Custom items cannot contain commas.');
                                    }
                                    else {
                                        setOptionsWithCustom([...optionsWithCustom, enteredValue]);
                                        message.success('New option created. Please select it to add it to your list of responses.')
                                    }
                                }}
                            >
                                <PlusOutlined /> Add item
                            </a>
                        </div>
                    </div>
                )}
            >
                {optionsWithCustom.map((item) => (
                    <Option key={item}>{item}</Option>
                ))}
            </Select>
        );
    }

    function MultichoiceQuestion(question_id, question, options, multiple, question_category) {
        const allowOptOut = question_category !== "platform-use";

        const [selectedValue, setSelectedValue] = useState(
            question_category === "platform-use"
                ? usageForm.getFieldValue(question_id)
                : demographicsForm.getFieldValue(question_id)
        );

        const [selectDisabled, setSelectDisabled] = useState(
            allowOptOut
                ? (question_category === "platform-use"
                    ? usageForm.getFieldValue(question_id + "PreferNotToSay")
                    : demographicsForm.getFieldValue(question_id + "PreferNotToSay"))
                : false
        );

        return (
            <div className="SurveyQuestionPadding" style={{ clear: "both" }}>
                <Text>{question}</Text>

                <div>
                    <div className={useMobileVersion ? "SurveyQuestionMobile" : "SurveySelect"}>
                        <Form.Item name={question_id} preserve>
                            {multiple
                                ? SelectAllDropdown(options, selectDisabled)
                                : SelectOneDropdown(options, setSelectedValue, selectDisabled)}
                        </Form.Item>
                    </div>

                    {allowOptOut && (
                        <div>
                            {OptOutCheckbox(question_id, setSelectDisabled, question_category)}
                        </div>
                    )}
                </div>

                {selectedValue === "Other: Specify" && !selectDisabled && (
                        <div style={{ marginTop: "0.5em", clear: "both" , width: "25%"}}>
                            <Form.Item
                                name={question_id + "CustomValue"}
                                label="Enter custom value:"
                                className="SurveyQuestionPadding"
                            >
                                <Input />
                            </Form.Item>
                        </div>
                )}
            </div>
        );
    }


    function SurveyQuestion({ question_id, question, question_type, options, survey_platform, question_category }) {
        const applies = (survey_platform === platform || survey_platform === "chatbots");
        if (!applies) return null;

        switch (question_type) {
            case 'point-scale':
                return SliderQuestion(question_id, question, options, question_category);
            case 'multichoice':
                return MultichoiceQuestion(question_id, question, options, false, question_category);
            case 'select_all_apply':
                return MultichoiceQuestion(question_id, question, options, true, question_category);
            default:
                return null;
        }
    }

function skipSurvey() {
        //console.log("User chose to skip survey.");

        // Basic guards
        if (!platform || !emailIdentifier) {
            openNotificationCannotSkip();
            return;
        }

        const email_md5 = md5(emailIdentifier);

        // Verify survey completion from backend.
        const url = `payments/email_md5?email_md5=${email_md5}&platform=${platform}`;

        FetchFromBackend(
            url,
            () => {}, // success callback handled in setter below
            () => {
                // If the check fails, be conservative: don't allow skipping.
                openNotificationCannotSkip();
            },
            (result) => {
                if (result.exists && result.survey_completed === true) {
                    next(); // go straight to Consent 
                } else {
                    openNotificationCannotSkip();
                }
            }
        );
}
    function SurveyDoneScreen() {
        return (
            <div hidden={surveyStage !== "done"} style={{ width: "100%", display: "flex", justifyContent: "center" }}>
            <div style={{ maxWidth: 720, width: "100%", padding: useMobileVersion ? "8px" : "24px" }}>
                <Result
                status="success"
                title="Survey completed"
                subTitle="You can now continue to the consent form and donate your data."
                extra={[
                    <div key="codebox" style={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: 8,
                    marginBottom: 8
                    }}>
                    <div style={{
                        padding: useMobileVersion ? "12px 14px" : "16px 18px",
                        border: "1px dashed #d9d9d9",
                        borderRadius: 12,
                        width: "fit-content",
                        textAlign: "center"
                    }}>
                        <Text style={{ display: "block", marginBottom: 8 }}>
                        <strong>Your completion code</strong>
                        </Text>

                        <Text
                        code
                        style={{
                            display: "inline-block",
                            fontSize: useMobileVersion ? 18 : 22,
                            padding: useMobileVersion ? "8px 10px" : "10px 14px",
                            letterSpacing: 2
                        }}
                        >
                        {completionCode || "—"}
                        </Text>

                        <Text style={{ display: "block", marginTop: 10, opacity: 0.75 }}>
                            Use this code to register your participation on Prolific.
                        </Text>
                    </div>
                    </div>,

                    <Button
                    key="continue"
                    type="primary"
                    size="large"
                    onClick={() => {
                        next(); // proceed to Consent step
                        window.scrollTo(0, 0);
                    }}
                    >
                    Continue to Donation
                    </Button>
                ]}
                />
            </div>
            </div>
        );
    }





    return (
        <div align="center">
            <Form name="DemographicsSurvey" onFinish={onFinishDemographics} layout="vertical" form={demographicsForm}>
            <div hidden={!(surveyStage === "demographics")}>
                <Form.Item className="TopMargin">
                        <Button
                            className="RightMargin"
                            onClick={skipSurvey}>
                            Skip Survey
                        </Button>
                </Form.Item>
            </div>
                <div hidden={!(surveyStage === "demographics")}>
                    <Card
                        align="left">
                        <Card.Meta title="Demographics Survey (Survey Part 1/2)" />
                        <Divider />
                            {demographicSurveyQuestions.map((item) => (
                                <SurveyQuestion key={item.question_id} {...item} />
                            ))}
                    </Card>
                    <Form.Item className="TopMargin">
                        <Button
                            type="primary"
                            htmlType="submit">
                            To Usage questions
                        </Button>
                    </Form.Item>
                    
                </div>
            </Form >

            <Form name="UsageSurvey" onFinish={onFinishUsage} layout="vertical" form={usageForm}>
                <div hidden={!(surveyStage === "usage")}>
                    <Card
                        align="left">
                        <Card.Meta
                            title={(platform === "chatgpt" ? "ChatGPT" : platform === "gemini" ? "Gemini" : platform === "claude" ? "Claude" : platform === "deepseek" ? "DeepSeek" : platform === 'mistral' ? "Mistral" : platform === 'grok' ? "Grok" : "") + " Use Survey (Survey Part 2/2)"}
                            description="Please fill out this survey to the best of your ability." />
                        <Divider />
                        {usageSurveyQuestions.map((item) => (
                            <SurveyQuestion key={item.question_id} {...item} />
                        ))}
                    </Card>
                    <Form.Item className="TopMargin">
                        <Button
                            className="RightMargin"
                            onClick={() => {setSurveyStage("demographics")}}
                            >
                            To Demographics Questions
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                        >
                            Finish Survey
                        </Button>
                    </Form.Item>
                </div>
            </Form >

            <SurveyDoneScreen />
        </div>
    )
};

export default FormSurvey;